import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  IonGrid, IonRow, IonCol, IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonChip, IonText,
  IonButton, IonIcon, IonList, IonItem, IonLabel, IonAvatar, IonSegment, IonSegmentButton, IonSpinner, IonContent,
  IonInput,
} from "@ionic/react";
import { checkmarkOutline, arrowDownOutline, personOutline, cardOutline, briefcaseOutline, receiptOutline } from "ionicons/icons";
import FreshPopover from "@/components/FreshPopover";
import { toastText } from "@/utils/toastTemplates";
import PaymentMethodsModal from "@/components/PaymentMethodsModal";
import InvoicesModal from "@/components/InvoicesModal";
import NativeBilling from "@/components/NativeBilling";
import AppLayout from "@/components/AppLayout";
import { uiAvatar } from "@/components/SidebarProfileCard";
import { userHeaders, isUserLoggedIn } from "@/utils/userAuth";
import { useAppLive } from "@/utils/appLive";
import { toast } from "@/utils/toast";
import { Capacitor } from "@capacitor/core";
import { confirmAlert } from "@/utils/confirmAlert";
import SubscriptionCheckoutModal from "@/components/SubscriptionCheckoutModal";
import BusinessSetupModal from "@/components/BusinessSetupModal";
import { fetchSavedAssets, invalidateSavedAssets, useSavedAssets } from "@/components/SavedAssets";
import BusinessSwitcherPopover from "@/components/BusinessSwitcher";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// The segment row in the top bar (the Preferences pages' pattern): the plans,
// and the account balance. ?segment=wallet opens the balance.
const SEGMENTS = [
  { value: "plans", label: "Subscription Plans", to: "/app/billing" },
  { value: "wallet", label: "Wallet Balance", to: "/app/billing?segment=wallet" },
];

// /app/billing: the current plan, the plans (monthly / annual) with their key
// features, and the full comparison table. The catalog comes from the admin's
// Subscriptions page; Stripe handles checkout, plan changes and the portal.
// The Wallet Balance segment holds the account balance card.
// In the iOS and Android app nothing is sold (store rules): NativeBilling
// shows the plan and balance and links out to this page on the website.
export default function AppBilling() {
  return Capacitor.isNativePlatform?.() ? <NativeBilling /> : <WebBilling />;
}

function WebBilling() {
  const navigate = useNavigate();
  const location = useLocation();
  const [catalog, setCatalog] = useState(null); // { plans, sections }
  const [interval, setInterval_] = useState("month");
  const [status, setStatus] = useState(null); // /api/stripe/subscription-status (signed in)
  const [busy, setBusy] = useState(null);
  const [checkoutPlan, setCheckoutPlan] = useState(null); // the plan being paid for in the checkout modal
  const [acctMenu, setAcctMenu] = useState({ open: false, event: undefined }); // the account row popover
  const [bizSwitcher, setBizSwitcher] = useState({ open: false, event: undefined }); // the business row popover
  const [pmOpen, setPmOpen] = useState(false); // the Payment methods modal
  const [invOpen, setInvOpen] = useState(false); // the Invoices modal
  const signedIn = isUserLoggedIn();
  const userInfo = (() => { try { return JSON.parse(localStorage.getItem("userInfo") || "null"); } catch { return null; } })();

  const loadCatalog = useCallback(async () => {
    try {
      const d = await (await fetch(`${BACKEND_URL}/api/subscriptions/catalog`)).json();
      if (d.success) setCatalog({ plans: d.plans || [], sections: d.sections || [] });
    } catch { /* keep what we have */ }
  }, []);
  const loadStatus = useCallback(async () => {
    if (!isUserLoggedIn()) { setStatus({ hasSubscription: false }); return; }
    try {
      const d = await (await fetch(`${BACKEND_URL}/api/stripe/subscription-status`, { headers: userHeaders() })).json();
      setStatus(d && d.success ? d : { hasSubscription: false });
    } catch { setStatus({ hasSubscription: false }); }
  }, []);
  useEffect(() => { loadCatalog(); loadStatus(); }, [loadCatalog, loadStatus]);

  // The account balance: loaded here with any amount through Stripe;
  // documents then spend their exact price from it (the native app links
  // out to this page). Different documents cost different prices, so a
  // custom amount replaces fixed packs.
  const [wallet, setWallet] = useState(null); // { balance, min, max }
  const [loadAmount, setLoadAmount] = useState("");
  const [walletCheckout, setWalletCheckout] = useState(null); // { amount } being paid for in the checkout modal
  const loadWallet = useCallback(async () => {
    if (!isUserLoggedIn()) { setWallet({ balance: 0, min: 5, max: 500 }); return; }
    try {
      const d = await (await fetch(`${BACKEND_URL}/api/wallet`, { headers: userHeaders() })).json();
      if (d.success) {
        setWallet(d);
        // the cached account the generators read their balance chip from
        try { const u = JSON.parse(localStorage.getItem("userInfo") || "null"); if (u) { u.walletCents = Math.round((d.balance || 0) * 100); localStorage.setItem("userInfo", JSON.stringify(u)); } } catch { /* cache only */ }
      }
    } catch { setWallet({ balance: 0, min: 5, max: 500 }); }
  }, []);
  useEffect(() => { loadWallet(); }, [loadWallet]);
  // A quick amount or the custom one opens the checkout modal (the plan
  // checkout's twin) for exactly that amount
  const loadFunds = (amountOverride) => {
    if (!signedIn) { navigate("/login"); return; }
    const amount = parseFloat(amountOverride != null ? amountOverride : loadAmount);
    const min = wallet?.min ?? 5, max = wallet?.max ?? 500;
    if (!amount || isNaN(amount) || amount < min || amount > max) {
      toast.error(`Enter an amount between $${min} and $${max}`);
      return;
    }
    setWalletCheckout({ amount: Math.round(amount * 100) / 100 });
  };
  useAppLive(() => { loadCatalog(); loadStatus(); loadWallet(); });

  // ?business=<id>: the plan being chosen here is that business's own
  // subscription, not the account's. The switcher's summaries name it.
  const params = new URLSearchParams(location.search);
  const forBusinessId = params.get("business") || null;
  const { businesses, business: activeBusiness } = useSavedAssets();
  const forBusiness = forBusinessId ? businesses.find((b) => b.id === forBusinessId) || null : null;
  // The balance is loaded on the web only (the native app links out here)
  // and never while a business's plan is being chosen, so the segment row
  // only appears where the Wallet Balance segment has something to show.
  // a balance belongs to an account, so guests only see the plans
  const walletAvailable = !Capacitor.isNativePlatform?.() && !forBusinessId && signedIn;
  const segment = walletAvailable && params.get("segment") === "wallet" ? "wallet" : "plans";
  // A guest sent to the balance (the app's "load a balance" link opens here
  // in the browser, signed out): sign in first, then land on it
  useEffect(() => {
    if (!signedIn && !Capacitor.isNativePlatform?.() && params.get("segment") === "wallet") {
      navigate(`/login?next=${encodeURIComponent(`/app/billing${location.search}`)}`, { replace: true });
    }
  }, [signedIn, location.search]); // eslint-disable-line

  // Back from Stripe checkout
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("checkout") === "success") {
      navigate("/app/billing", { replace: true });
      toast.success(toastText("subscription_started", {}, "Subscription started. Welcome aboard!"));
      setTimeout(loadStatus, 1500);
    }
  }, [location.search]); // eslint-disable-line

  // Buying a plan FOR A BUSINESS: only Pro and Enterprise carry the business
  // features, so Express stays off that page entirely.
  const allPlans = catalog?.plans || [];
  const plans = forBusinessId ? allPlans.filter((p) => ["pro", "enterprise"].includes(p.id)) : allPlans;
  const sections = catalog?.sections || [];
  const currentTier = status?.hasSubscription ? status.tier : (userInfo?.subscription?.status === "active" ? userInfo.subscription.tier : null);
  const currentPlan = plans.find((p) => p.id === currentTier);
  // In business mode the cards compare against the TARGET business's own
  // plan, not the account's: a brand-new business has none, so every plan is
  // there to be bought for it — whatever the account happens to be on.
  const LIVE_STATUSES = ["active", "trialing", "cancelling"];
  const targetTier = forBusinessId
    ? ((forBusiness && LIVE_STATUSES.includes(forBusiness.status)) ? forBusiness.tier : null)
    : currentTier;
  const subState = (() => {
    if (!signedIn) return { chip: "Signed out", color: "medium", note: "" };
    if (!status) return { chip: "Checking…", color: "medium", note: "" };
    if (!status.hasSubscription) return { chip: "No plan", color: "danger", note: "" };
    const end = status.currentPeriodEnd ? new Date(status.currentPeriodEnd) : null;
    const endLong = end ? end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
    const days = end ? Math.max(0, Math.ceil((end - Date.now()) / 86400000)) : null;
    if (status.status === "trialing") return { chip: "Trialing", color: "warning", note: days != null ? `${days} day${days === 1 ? "" : "s"} left` : "" };
    if (status.cancelAtPeriodEnd || status.status === "cancelling") return { chip: "Cancelling", color: "warning", note: days != null ? `${days} day${days === 1 ? "" : "s"} left` : "" };
    if (status.status === "past_due" || status.status === "unpaid") return { chip: "Past due", color: "danger", note: end ? `Renews on ${endLong}` : "" };
    if (status.status === "active") return { chip: "Active", color: "success", note: !status.adminAssigned && end ? `Renews on ${endLong}` : "" };
    return { chip: status.status || "Inactive", color: "medium", note: "" };
  })();

  // No live plan on a signed-in account: the whole current-plan card turns
  // danger, like a trial-expired wall, with the plans right below to fix it.
  const noSub = signedIn && !!status && !status.hasSubscription;

  const post = async (path, body) => {
    const res = await fetch(`${BACKEND_URL}${path}`, { method: "POST", headers: { ...userHeaders(), "Content-Type": "application/json" }, body: body ? JSON.stringify(body) : undefined });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) throw new Error(data.detail || data.error || "Something went wrong");
    return data;
  };

  // Right after a Pro or Enterprise plan comes alive: offer the business
  // setup once, and only while there is no business saved yet. The sidebar's
  // business card keeps offering it to anyone who skips here.
  const [bizSetupOpen, setBizSetupOpen] = useState(false);
  const offerBusinessSetup = async (planId) => {
    if (!["pro", "enterprise"].includes(planId)) return;
    invalidateSavedAssets();
    const d = await fetchSavedAssets(true);
    if (d.hasAccess && !(d.business && d.business.name)) setBizSetupOpen(true);
  };

  const activate = async (plan) => {
    if (!signedIn) { navigate("/login"); return; }
    setBusy(plan.id);
    try {
      if (forBusinessId) {
        // a business's own plan is always a fresh subscription, never a
        // switch of the account's
        setCheckoutPlan(plan);
        return;
      }
      // Dropping the account to Express with a business on it: eyes open.
      // Business features pause on Express; the data stays for a return to
      // Pro or Enterprise. (A business running its own plan blocks this
      // server-side until that plan is cancelled.)
      if (plan.id === "express" && (businesses.length > 0 || activeBusiness?.name)) {
        if (!(await confirmAlert({
          header: "Express has no business features",
          message: "Your business profile, employees, recurring pay stubs, branding and signatures pause on Express. Everything is kept and returns when you are back on Pro or Enterprise.",
          confirmText: "Switch anyway",
          cancelText: "Keep my plan",
        }))) { setBusy(null); return; }
      }
      if (status?.hasSubscription && !status.adminAssigned) { // a plan the admin assigned has no Stripe subscription to switch; Activate starts one
        const label = `${plan.name} (${money(interval === "year" ? plan.annual : plan.monthly)}/${interval === "year" ? "yr" : "mo"})`;
        if (!(await confirmAlert({ header: `Switch to ${plan.name}?`, message: `Your subscription changes to ${label}. Stripe prorates the difference.`, confirmText: "Switch" }))) return;
        await post("/api/stripe/change-subscription", { tier: plan.id, interval });
        toast.success(toastText("plan_switched", { plan: plan.name }, "Switched to {plan}"));
        await loadStatus();
        await offerBusinessSetup(plan.id);
      } else {
        setCheckoutPlan(plan); // the card checkout happens in a modal, not on Stripe's page
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };
  const cancel = async () => {
    if (!(await confirmAlert({ header: "Cancel your subscription?", message: "You keep access until the end of the billing period.", confirmText: "Cancel plan" }))) return;
    setBusy("cancel");
    try { await post("/api/stripe/cancel-subscription"); toast.success(toastText("subscription_cancelled_period", {}, "Your plan ends at the end of the billing period")); await loadStatus(); }
    catch (err) { toast.error(err.message); } finally { setBusy(null); }
  };
  // A plan the admin assigned has no paid period, so cancelling ends it at once
  const cancelAssigned = async () => {
    if (!(await confirmAlert({ header: "Cancel your subscription?", message: "Your plan ends right away and any remaining downloads are removed.", confirmText: "Cancel subscription", cancelText: "Keep plan" }))) return;
    setBusy("cancel");
    try { await post("/api/stripe/cancel-subscription"); toast.success(toastText("subscription_cancelled_now", {}, "Your subscription has been cancelled")); await loadStatus(); }
    catch (err) { toast.error(err.message); } finally { setBusy(null); }
  };
  const reactivate = async () => {
    const renews = status?.currentPeriodEnd ? new Date(status.currentPeriodEnd).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
    if (!(await confirmAlert({ header: "Reactivate your subscription?", message: renews ? `Your plan stays on and renews on ${renews}, billed as usual.` : "Your plan stays on and keeps renewing, billed as usual.", confirmText: "Reactivate", cancelText: "Not now" }))) return;
    setBusy("reactivate");
    try { await post("/api/stripe/reactivate-subscription"); toast.success(toastText("subscription_reactivated", {}, "Subscription reactivated")); await loadStatus(); }
    catch (err) { toast.error(err.message); } finally { setBusy(null); }
  };
  const portal = async () => {
    // A store-bought plan is managed in the store, not the Stripe portal
    const prov = (() => { try { return (JSON.parse(localStorage.getItem("userInfo") || "null") || {}).subscription?.provider; } catch { return null; } })();
    if (prov === "apple") { window.open("https://apps.apple.com/account/subscriptions", "_blank"); return; }
    if (prov === "google") { window.open("https://play.google.com/store/account/subscriptions", "_blank"); return; }
    setBusy("portal");
    try { const d = await post("/api/billing/portal"); if (d.url) window.location.href = d.url; }
    catch (err) { toast.error(err.message); } finally { setBusy(null); }
  };

  const price = (p) => (interval === "year" ? `${money(p.annual)}/yr` : `${money(p.monthly)}/mo`);
  const accountName = userInfo?.name || userInfo?.email || "Guest";

  return (
    <AppLayout
      pageTabs={walletAvailable ? {
        value: segment,
        items: SEGMENTS,
        // replace, not push: segment hops stay one history entry, so the top
        // bar's back leaves to the page you actually came from
        onChange: (v) => { const s = SEGMENTS.find((x) => x.value === v); if (s) navigate(s.to, { replace: true }); },
      } : undefined}
    >
      {checkoutPlan && (
        <SubscriptionCheckoutModal
          plan={checkoutPlan}
          interval={interval}
          businessId={forBusinessId || undefined}
          onClose={() => setCheckoutPlan(null)}
          onActivated={async () => {
            const planId = checkoutPlan.id;
            setCheckoutPlan(null);
            if (forBusinessId) {
              toast.success(toastText("business_plan_activated", { plan: checkoutPlan.name, business: forBusiness?.name || "your business" }, "{plan} is active for {business}"));
              invalidateSavedAssets();
              navigate("/app/billing", { replace: true });
              return;
            }
            toast.success(toastText("plan_activated", { plan: checkoutPlan.name }, "Your {plan} plan is active. Welcome aboard!"));
            await loadStatus();
            await offerBusinessSetup(planId);
          }}
        />
      )}
      {walletCheckout && (
        /* the Wallet Balance card's checkout: the plan checkout modal in its
           top-up form, "Pay as you go" at the chosen amount */
        <SubscriptionCheckoutModal
          wallet={walletCheckout}
          onClose={() => setWalletCheckout(null)}
          onActivated={async () => {
            setWalletCheckout(null);
            setLoadAmount("");
            toast.success("Your account has been loaded");
            await loadWallet();
          }}
        />
      )}
      {/* the account row popover, the same as the sidebar profile card popover */}
      <FreshPopover open={acctMenu.open} event={acctMenu.event} onClose={() => setAcctMenu({ open: false, event: undefined })} side="bottom" alignment="start" style={{ "--width": "284px", "--offset-y": "4px" }}>
        <IonContent>
          <IonList lines="none" className="account-menu" style={{ padding: "4px 0" }}>
            <IonItem button detail={false} onClick={() => { setAcctMenu({ open: false, event: undefined }); navigate("/app/account"); }} style={{ "--min-height": "44px", "--padding-start": "14px", "--inner-padding-end": "14px", fontSize: "0.88rem" }}>
              <div slot="start" style={{ display: "inline-flex", alignItems: "center", marginRight: 10 }}><IonIcon icon={personOutline} style={{ fontSize: 18 }} /></div>
              <IonLabel>Manage profile</IonLabel>
            </IonItem>
            <IonItem button detail={false} onClick={() => { setAcctMenu({ open: false, event: undefined }); setPmOpen(true); }} style={{ "--min-height": "44px", "--padding-start": "14px", "--inner-padding-end": "14px", fontSize: "0.88rem" }}>
              <div slot="start" style={{ display: "inline-flex", alignItems: "center", marginRight: 10 }}><IonIcon icon={cardOutline} style={{ fontSize: 18 }} /></div>
              <IonLabel>Payment methods</IonLabel>
            </IonItem>
            <IonItem button detail={false} onClick={() => { setAcctMenu({ open: false, event: undefined }); setInvOpen(true); }} style={{ "--min-height": "44px", "--padding-start": "14px", "--inner-padding-end": "14px", fontSize: "0.88rem" }}>
              <div slot="start" style={{ display: "inline-flex", alignItems: "center", marginRight: 10 }}><IonIcon icon={receiptOutline} style={{ fontSize: 18 }} /></div>
              <IonLabel>Invoices</IonLabel>
            </IonItem>
          </IonList>
        </IonContent>
      </FreshPopover>
      {pmOpen && <PaymentMethodsModal onClose={() => setPmOpen(false)} />}
      {invOpen && <InvoicesModal onClose={() => setInvOpen(false)} />}
      <BusinessSwitcherPopover open={bizSwitcher.open} event={bizSwitcher.event} onClose={() => setBizSwitcher({ open: false, event: undefined })} />
      {bizSetupOpen && <BusinessSetupModal onClose={() => setBizSetupOpen(false)} />}
      <div className="app-billing" style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* ── Subscription Plans: the current plan, the plans, the comparison table ── */}
        {segment === "plans" && (<>
        {forBusiness && (
          <div className="biz-plan-banner" style={{ maxWidth: 760, margin: "14px auto 0", padding: "12px 16px", borderRadius: 8, background: "var(--ion-card-background)", border: "1px solid var(--app-divider, rgba(0,0,0,0.08))", display: "flex", alignItems: "center", gap: 10 }}>
            <IonIcon icon={briefcaseOutline} style={{ fontSize: 20, color: "var(--ion-color-primary)", flexShrink: 0 }} />
            <span style={{ fontSize: "0.9rem" }}>
              Choosing a plan for <strong>{forBusiness.name}</strong>. Each business runs on its own subscription.
            </span>
          </div>
        )}
        <h1 className="ion-padding-horizontal ion-text-center">Current Plan</h1>
        <IonGrid>
          <IonRow>
            <IonCol size="12" sizeMd="" className="billing-side" />
            <IonCol size="12" sizeMd="">
              {/* the global danger palette is the house #f04141 now; no per-card override needed */}
              <IonCard className="plan-card current-plan-card" color={noSub ? "danger" : undefined}>
                {!noSub && <IonChip color={subState.color} className="header-chip bold">{subState.chip}</IonChip>}
                <IonCardHeader>
                  <IonCardTitle>
                    <IonText>{noSub ? "No Active Subscription Plan" : currentPlan ? currentPlan.name : currentTier ? currentTier.charAt(0).toUpperCase() + currentTier.slice(1) : signedIn ? "No active plan" : "Sign in to subscribe"}</IonText>
                  </IonCardTitle>
                  {subState.note && <p className="plan-note" style={{ margin: "4px 0 0", fontSize: "0.85rem", fontWeight: 500, color: "var(--ion-color-medium)" }}>{subState.note}</p>}
                </IonCardHeader>
                {noSub && (
                  <p style={{ margin: "0 16px 16px", fontSize: "0.95rem", lineHeight: 1.5 }}>
                    {status.cancelReason === "payment_failed"
                      ? "Your last payment didn't go through, so the plan was cancelled. Pick a plan below to start again."
                      : "You don't have an active plan. To continue using MintSlip's subscription features, pick a plan below that meets your needs."}
                  </p>
                )}
                <div className="ion-padding-horizontal ion-padding-bottom">
                  <IonCard className="business-employee-card" style={{ margin: 0 }}>
                    <IonList>
                      {/* The business being operated as, above the account row
                          like the sidebar: tapping it opens the switcher, so
                          whose plan this page manages is changed right here */}
                      {signedIn && businesses.length > 0 && (
                        <IonItem button detail lines="full" onClick={(e) => setBizSwitcher({ open: true, event: e.nativeEvent })}>
                          <IonAvatar slot="start"><img alt="" src={activeBusiness?.logo || uiAvatar(activeBusiness?.name || "Business")} /></IonAvatar>
                          <IonLabel><p>Business</p>{activeBusiness?.name || "Add your business"}</IonLabel>
                        </IonItem>
                      )}
                      <IonItem button detail onClick={(e) => (signedIn ? setAcctMenu({ open: true, event: e.nativeEvent }) : navigate("/login"))}>
                        <IonAvatar slot="start"><img alt="" src={userInfo?.photo || uiAvatar(accountName)} /></IonAvatar>
                        <IonLabel><p>Account</p>{accountName}</IonLabel>
                      </IonItem>
                    </IonList>
                  </IonCard>
                  {signedIn && status?.hasSubscription && status.adminAssigned && (
                    <>
                      <div className="current-plan-actions">
                        <IonButton expand="block" color="danger" onClick={cancelAssigned} disabled={busy === "cancel"}>Cancel subscription</IonButton>
                      </div>
                      <p className="plan-admin-note">Pick a plan below to move to a paid subscription.</p>
                    </>
                  )}
                  {signedIn && status?.hasSubscription && !status.adminAssigned && (
                    <div className="current-plan-actions">
                      {(status.cancelAtPeriodEnd || status.status === "cancelling")
                        ? <IonButton expand="block" color="success" onClick={reactivate} disabled={busy === "reactivate"}>Reactivate</IonButton>
                        : <IonButton expand="block" color="danger" onClick={cancel} disabled={busy === "cancel"}>Cancel plan</IonButton>}
                    </div>
                  )}
                  {!signedIn && (
                    <div className="current-plan-actions">
                      <IonButton expand="block" color="success" onClick={() => navigate("/login")}>Sign in</IonButton>
                    </div>
                  )}
                  {noSub && (
                    <div className="ion-text-center" style={{ marginTop: 6 }}>
                      <IonButton fill="clear" style={{ "--color": "#ffffff", color: "#ffffff", fontWeight: 600 }}
                        onClick={() => window.dispatchEvent(new CustomEvent("app-logout"))}>Log out</IonButton>
                    </div>
                  )}
                </div>
              </IonCard>
            </IonCol>
            <IonCol size="12" sizeMd="" className="billing-side" />
          </IonRow>
        </IonGrid>

        <IonSegment mode="ios" className="interval-segment" value={interval} onIonChange={(e) => setInterval_(e.detail.value)} style={{ margin: "24px auto", width: 200 }}>
          <IonSegmentButton value="month"><IonLabel>Monthly</IonLabel></IonSegmentButton>
          <IonSegmentButton value="year"><IonLabel>Annual</IonLabel></IonSegmentButton>
        </IonSegment>

        <IonGrid>
          <h1 className="ion-padding-horizontal ion-text-center">{interval === "year" ? "Annual Plans" : "Monthly Plans"}</h1>
          {!catalog && <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><IonSpinner name="crescent" /></div>}
          <IonRow>
            {plans.map((plan, i) => {
              const isCurrent = targetTier === plan.id;
              const label = isCurrent
                ? "Current plan"
                : busy === plan.id
                  ? "Working…"
                  : forBusinessId
                    ? "Activate" // a business's plan is always a fresh subscription
                    : status?.hasSubscription && !status.adminAssigned ? `Switch to ${plan.name}` : "Activate";
              return (
                <IonCol key={plan.id} size="12" sizeMd="">
                  <IonCard className={`plan-card${isCurrent ? " is-current" : ""}`}>
                    <IonChip color={isCurrent ? "success" : "danger"} className="header-chip bold">{isCurrent ? "Active" : "Inactive"}</IonChip>
                    <IonCardHeader>
                      <IonCardTitle style={{ display: "flex", justifyContent: "space-between" }}>
                        {plan.name}
                        <IonText className="ion-text-right"><span>{price(plan)}</span></IonText>
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent className="ion-text-center">
                      <IonButton expand="block" color="success" disabled={isCurrent || !!busy} onClick={() => activate(plan)}>{label}</IonButton>
                    </IonCardContent>
                    <div className="workers-container">
                      <IonCard className="workers-card" style={{ marginTop: 0 }}>
                        {/* the shared sub-header bar (same as the admin sub-headers) */}
                        <div className="admin-subbar">{i === 0 ? "Key Features" : `Everything from ${plans[i - 1].name}, plus...`}</div>
                        <IonList>
                          {(plan.features || []).map((f, j) => (
                            <IonItem key={j}><IonIcon slot="start" icon={checkmarkOutline} /><IonLabel className="ion-text-wrap">{f}</IonLabel></IonItem>
                          ))}
                          <IonItem lines="none" button detail={false} onClick={() => document.querySelector(".features-card")?.scrollIntoView({ behavior: "smooth", block: "start" })}>
                            <IonIcon slot="start" icon={arrowDownOutline} className="flip-rtl" /><IonLabel className="bold">See complete list below</IonLabel>
                          </IonItem>
                        </IonList>
                      </IonCard>
                    </div>
                  </IonCard>
                </IonCol>
              );
            })}
          </IonRow>

          {sections.length > 0 && (
            <IonRow>
              <IonCol>
                <IonCard className="features-card">
                  {/* on phones the table keeps its full-size columns and scrolls side to side inside the card */}
                  <div className="features-scroll">
                  <IonGrid>
                    <IonRow className="ion-align-items-center ion-nowrap features-head">
                      <IonCol size="6"><IonCardHeader><IonCardTitle>Features</IonCardTitle></IonCardHeader></IonCol>
                      {plans.map((p) => <IonCol key={p.id} size="2" className="ion-text-center plan-col-name">{p.name}</IonCol>)}
                    </IonRow>
                    {sections.map((sec, si) => (
                      <div key={sec.id || si}>
                        <IonRow className="feature-row subbar-row ion-nowrap">
                          <div className="admin-subbar">{sec.title}</div>
                        </IonRow>
                        {(sec.rows || []).map((r, ri) => (
                          <IonRow key={r.id || ri} className="feature-row ion-align-items-center ion-nowrap">
                            <IonCol size="6">{r.label}</IonCol>
                            {plans.map((p) => {
                              const v = (r.values || {})[p.id];
                              return <IonCol key={p.id} size="2" className="ion-text-center">{v === true ? <IonIcon icon={checkmarkOutline} /> : v ? v : null}</IonCol>;
                            })}
                          </IonRow>
                        ))}
                      </div>
                    ))}
                  </IonGrid>
                  </div>
                </IonCard>
              </IonCol>
            </IonRow>
          )}
        </IonGrid>
        </>)}

        {/* ── Wallet Balance: the account balance, loaded with any amount ── */}
        {segment === "wallet" && (
          <IonGrid>
            <h1 className="ion-padding-horizontal ion-text-center">Wallet Balance</h1>
            {/* the Current Plan card's row: the same side columns, so both cards share a width */}
            <IonRow>
              <IonCol size="12" sizeMd="" className="billing-side" />
              <IonCol size="12" sizeMd="">
                <IonCard className="plan-card">
                  <IonCardHeader>
                    <IonCardTitle><IonText>Pay as you go</IonText></IonCardTitle>
                    <p className="plan-note" style={{ margin: "4px 0 0", fontSize: "0.85rem", fontWeight: 500, color: "var(--ion-color-medium)" }}>
                      Load your account with any amount. Each document simply uses its exact price. No subscription.
                    </p>
                  </IonCardHeader>
                  <div style={{ padding: "0 16px 16px" }}>
                    {/* the balance, centred */}
                    <div style={{ textAlign: "center", padding: "6px 0 16px" }}>
                      <div style={{ fontSize: "0.85rem", color: "var(--ion-color-medium)" }}>Current balance</div>
                      <div style={{ fontSize: "1.7rem", fontWeight: 700, lineHeight: 1.2 }}>${Number(wallet?.balance || 0).toFixed(2)}</div>
                    </div>
                    {/* the quick amounts: circles in the brand colour (the
                        user's own when they picked one), centred, with the
                        stock Ionic ripple on tap */}
                    <div className="wallet-quick-amounts" style={{ display: "flex", justifyContent: "center", gap: 14, marginBottom: 16 }}>
                      {[10, 25, 50].map((a) => (
                        <IonButton key={a} shape="round" color="primary" onClick={() => loadFunds(a)} aria-label={`Load $${a}`}
                          /* the theme pins every ion-button's --border-radius, which beats shape="round"'s own; 50% on the element restores the circle */
                          style={{ width: 64, height: 64, margin: 0, "--border-radius": "50%", "--padding-start": "0", "--padding-end": "0", "--color": "#ffffff", fontSize: "0.95rem", fontWeight: 700, letterSpacing: "0.01em", textTransform: "none" }}>
                          ${a}
                        </IonButton>
                      ))}
                    </div>
                    <IonInput fill="outline" labelPlacement="floating" label="Custom amount (USD)" type="number" inputmode="decimal"
                      value={loadAmount} onIonInput={(e) => setLoadAmount(e.detail.value ?? "")} />
                    {/* the Current Plan card's action row: full width under the
                        input, Ionic's success green, white uppercase label */}
                    <div className="current-plan-actions">
                      <IonButton expand="block" color="success" onClick={() => loadFunds()}>Load funds</IonButton>
                    </div>
                    <p style={{ margin: "8px 0 0", fontSize: "0.75rem", color: "var(--ion-color-medium)" }}>
                      Between ${wallet?.min ?? 5} and ${wallet?.max ?? 500}. Your balance never expires.
                    </p>
                  </div>
                </IonCard>
              </IonCol>
              <IonCol size="12" sizeMd="" className="billing-side" />
            </IonRow>
          </IonGrid>
        )}
      </div>
    </AppLayout>
  );
}
